import { useAdministrationContext } from '@openx/components-administration/lib/utils/context/AdministrationContext.tsx';
import { type AllTableCriteria, CriteriaDimension, SortDirection } from '@openx/types/tableCriteria.ts';
import { clientSideCriteriaProcessing } from '@openx/utils/lib/clientSideCriteriaProcessing.ts';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria.ts';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { TABLE_PAGE_SIZE } from '../../../config.ts';
import { useHandleListCriteriaOnCompleted } from '../../../hooks/useHandleListCriteriaOnCompleted.ts';
import { applyCustomFilter } from '../utils/applyCustomFilter.ts';

const defaultCriteria: AllTableCriteria = {
  [CriteriaDimension.FILTERS]: {
    account: '',
    fields: ['name', 'email_address'],
    phrase: '',
    role: '',
    status: '',
  },
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 100,
  },
  [CriteriaDimension.SORT]: {
    column: 'last_signin_time',
    direction: SortDirection.DESC,
    sortableColumns: ['name', 'email_address', 'role', 'is_active', 'last_signin_time', 'created_date'],
  },
};

export const useGetUsersDataAndCriteria = () => {
  const { useUsersListFetch } = useAdministrationContext();

  const [criteria, setCriteria] = useState<AllTableCriteria>(defaultCriteria);
  const handleCompleted = useHandleListCriteriaOnCompleted<AllTableCriteria>(setCriteria);
  const { users, error, loading } = useUsersListFetch({
    onCompleted: handleCompleted,
  });

  const mappedUsers = useMemo(() => {
    return clientSideCriteriaProcessing(users, criteria, {
      applyCustomFilter,
      onCriteriaAdjust: adjustedCriteria => setCriteria(adjustedCriteria),
    });
  }, [users, criteria]);

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (
        newCriteria?.dimension === 'sort' &&
        newCriteria.value?.column === criteria.sort?.column &&
        newCriteria.value?.direction === 'asc' &&
        newCriteria.value?.column !== defaultCriteria.sort?.column
      ) {
        newCriteria.value.column = defaultCriteria.sort?.column;
        newCriteria.value.direction = defaultCriteria.sort?.direction;
      }

      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual({ ...newCriteriaProcessed }, { ...criteria })) {
        setCriteria(newCriteriaProcessed);
      }
    },
    [criteria]
  );

  return {
    error,
    loading,
    users: mappedUsers,
    criteria,
    handleCriteriaChange,
  };
};
